import { useEffect, useState } from 'react';
import { BookingResponseDto } from 'client/widget-components/ecom/types/EcomSDK';

export type BookingStatus = 'pending' | 'accepted' | 'cancelled' | 'rejected';
export type BookingCreationStatus = 'PENDING' | 'FAILED' | 'SUCCESS';

/**
 * Which booking backend answers for this site, and where to reach it. Asked of ms-booking rather
 * than derived from `ecom.booking.calcom.duda.engine.enabled`: that flag is read once, when the
 * site's booking organization is created, and a later flip does not migrate it - so evaluating the
 * flag here can name a backend the site's bookings are not in. The path is relative because a
 * published site proxies `/rts/*` on its own runtime domain and never `/ms/*`.
 */
const BOOKING_CONFIG_URL = '/rts/booking/public/config';

interface BookingConfig {
    engine: 'NATIVE' | 'CAL_COM';
    /** Absolute for Cal.com, a relative `/rts/*` path for the native engine. */
    url: string;
}

async function fetchBookingConfig(): Promise<BookingConfig> {
    const response = await fetch(BOOKING_CONFIG_URL);
    if (!response.ok) {
        throw new Error(`booking config responded ${response.status}`);
    }
    return (await response.json()) as BookingConfig;
}

export interface FetchedBooking {
    id: string;
    startDate: Date;
    duration: number;
    timeZone: string;
    status: BookingStatus;
}

interface BookingDetailsState {
    booking: FetchedBooking | null;
    creationStatus?: BookingCreationStatus;
}

function getCreationStatus(
    input: BookingResponseDto
): BookingCreationStatus | undefined {
    if ('creation_status' in input && input.creation_status) {
        return input.creation_status;
    }
    if ('booking_status' in input && input.booking_status) {
        switch (input.booking_status) {
            case 'Pending':
                return 'PENDING';
            case 'Failed':
                return 'FAILED';
            case 'Success':
                return 'SUCCESS';
        }
    }
    return undefined;
}

export function useBookingDetails(
    bookingDto: BookingResponseDto | null
): BookingDetailsState {
    const creationStatus = bookingDto
        ? getCreationStatus(bookingDto)
        : undefined;

    const [booking, setBooking] = useState<FetchedBooking | null>(null);

    useEffect(() => {
        if (!bookingDto?.id) {
            setBooking(null);
            return;
        }

        let cancelled = false;

        const fetchBookingDetails = async () => {
            try {
                const { url: baseUrl } = await fetchBookingConfig();
                const endpoint = bookingDto.seat_id
                    ? `${baseUrl}/v2/bookings/by-seat/${bookingDto.seat_id}`
                    : `${baseUrl}/v2/bookings/${bookingDto.id}`;
                // The native engine routes `GET /v2/bookings/{uid}` on the mere presence of this
                // header: without it the request falls through to a legacy mapping returning Cal's
                // raw entity shape (`startTime`/`endTime`, uppercase status), which the parsing
                // below would silently misread.
                const response = await fetch(endpoint, {
                    headers: { 'cal-api-version': '2026-02-25' },
                });
                if (!response.ok) {
                    return;
                }
                const content = (await response.json()) as {
                    status: 'success' | 'error';
                    data: {
                        status: BookingStatus;
                        start: string;
                        duration: number;
                        attendees: Array<{ timeZone: string }>;
                    };
                    error?: unknown;
                };
                if (cancelled) return;
                if (content.error != undefined) {
                    return;
                }
                const attendeeTimeZone = content.data.attendees[0]?.timeZone;
                setBooking({
                    id: bookingDto.id!,
                    startDate: new Date(content.data.start),
                    duration: content.data.duration,
                    timeZone: attendeeTimeZone ?? bookingDto.time_zone,
                    status: content.data.status,
                });
            } catch {
                // swallow — booking notice will stay in "processing" mode
            }
        };

        void fetchBookingDetails();

        return () => {
            cancelled = true;
        };
    }, [bookingDto?.id, bookingDto?.seat_id, bookingDto?.time_zone]);

    return { booking, creationStatus };
}
